import { useEffect, useState } from 'react';

import {
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';
import {
  createAlert,
  getAlertsByFavoriteId,
  updateAlert,
} from 'src/services/alerts';
import { setBannerMessage } from 'src/store/display';
import { Alert, AlertType, Status } from 'src/types/alert';
import { Favorite } from 'src/types/favorite';

type AlertDialogProps = {
  title: string;
  favorite: Favorite;
  open: boolean;
  onClose: () => void;
};

type NotificationAlertType = {
  name: string;
  type: AlertType;
};

const notifications: NotificationAlertType[] = [
  {
    name: 'Assembleias',
    type: AlertType.NEW_CONVOCATION,
  },
  {
    name: 'Artigos',
    type: AlertType.NEW_ARTICLE,
  },
  {
    name: 'Documentos',
    type: AlertType.NEW_DOCUMENT,
  },
  {
    name: 'Fatos Relevantes',
    type: AlertType.NEW_RELEVANT_FACTS,
  },
];

export function AlertDialog({
  title,
  favorite,
  open,
  onClose,
}: AlertDialogProps) {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const [alerts, setAlerts] = useState<Alert[]>([]);

  const { isLoading } = useQuery({
    queryKey: ['alerts', favorite?.id],
    queryFn: () => getAlertsByFavoriteId(favorite?.id),
    onSuccess(data) {
      setAlerts(data.data);
    },
    enabled: open,
  });

  const createAlertMutation = useMutation(
    ({ type, favorite }: { type: AlertType; favorite: Favorite }) =>
      createAlert({
        favorite: favorite.id,
        type,
        status: Status.ENABLED,
        rule: {
          operationId: favorite.operation?.hashId?.toString() ?? '',
          documentType: type,
        },
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['alerts', favorite?.id],
        });
      },
      onError: () => {
        dispatch(
          setBannerMessage({
            message: 'Erro ao criar notificação',
            type: 'error',
          }),
        );
      },
    },
  );

  const updateAlertStatusMutation = useMutation(
    ({ alertId, status }: { alertId: number; status: Status }) =>
      updateAlert(alertId, status),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['alerts', favorite?.id],
        });
      },
      onError: () => {
        dispatch(
          setBannerMessage({
            message: 'Erro ao atualizar notificação',
            type: 'error',
          }),
        );
      },
    },
  );

  const handleToggleAlert = (type: AlertType, checked: boolean) => {
    const existsAlert = alerts?.find((alert) => alert.type === type);
    if (checked && !existsAlert) {
      createAlertMutation.mutate({
        type,
        favorite: favorite,
      });
    }
    if (existsAlert) {
      updateAlertStatusMutation.mutate({
        alertId: existsAlert.id,
        status: checked ? Status.ENABLED : Status.DISABLED,
      });
    }
  };

  useEffect(() => {}, [alerts]);

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle textAlign="center">{title}</DialogTitle>
      <DialogContent sx={{ px: 8, mt: 2 }}>
        {notifications.map((notification) => (
          <FormControlLabel
            key={notification.type}
            value={notification.type}
            control={
              <Switch
                color="primary"
                size="small"
                value={notification.type}
                checked={alerts?.some(
                  (alert) =>
                    alert.type === notification.type &&
                    alert.status === Status.ENABLED,
                )}
                onChange={(_, checked) => {
                  handleToggleAlert(notification.type, checked);
                }}
              />
            }
            label={notification.name}
            labelPlacement="bottom"
            sx={{
              '& .MuiFormControlLabel-label': {
                fontSize: '10px',
                whiteSpace: 'nowrap',
              },
              mx: 0.5,
            }}
          />
        ))}
      </DialogContent>
    </Dialog>
  );
}
